import {
  Autocomplete,
  Button,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import pluralize from 'pluralize';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SubscribersForm } from '../../component/batch/SubscribersForm';
import { CardHeaderFlex } from '../../component/cardHeaderFlex/CardHeaderFlex';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import ListTypography from '../../component/listTypography/ListTypography';
import TabPanel from '../../component/tabPanel/TabPanel';
import toastOptions from '../../helpers/toastOptions';
import useBooleanKeys from '../../hooks/useBooleanKeys';
import {
  batchReportCreate,
  batchReportRunGetList,
  getBatchReportDetails,
  getReportBatchById,
  getReportBatchDetailsFetching,
  getReportPossibleUsers,
  getReportUsersById,
  getReportUsersFetching,
} from '../../redux/slices/reports/reportsSlice';
import { getUserData } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { Card, CardFlex, CardHeaderTabs, GridCells } from '../../styled/components';
import { ReportHistory } from './ReportHistory';

export const ReportDetails = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, tab = 0 } = useParams();
  const tabValue = Number(tab);
  const fetchingDetails = useSelector(getReportBatchDetailsFetching);
  const fetchingUsers = useSelector(getReportUsersFetching);
  const report = useSelector(getReportBatchById(id ?? ''));
  const [modals, toggleModal] = useBooleanKeys({ showSubscribersForm: false, showConfirmDelete: false });
  const users = useSelector(getReportUsersById(id ?? ''));
  const userData = useSelector(getUserData);
  const [running, setRunning] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [testUser, setTestUser] = useState<{ id: string; label: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetching = fetchingDetails ?? fetchingUsers;

  // Distribution jobs fan out one email per project manager; dry run rehearses
  // that fan-out against a single test inbox. Only offered where the backend
  // supports it.
  const isDistribution = report?.short_id === 'pm-assignments';

  const testUserOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { id: string; label: string }[] = [];
    const add = (userId?: string, firstName?: string, lastName?: string, email?: string) => {
      if (!userId || !email || seen.has(userId)) return;
      seen.add(userId);
      options.push({ id: userId, label: `${[firstName, lastName].filter(Boolean).join(' ')} — ${email}` });
    };
    add(userData?.id, userData?.first_name, userData?.last_name, userData?.email);
    report?.recipients?.forEach((r) => add(r.user_id, r.first_name, r.last_name, r.email));
    users?.forEach((u) => add(u.user_id, u.first_name, u.last_name, u.email));
    return options;
  }, [userData, report?.recipients, users]);

  useEffect(() => {
    if (dryRun && !testUser && testUserOptions.length > 0) {
      setTestUser(testUserOptions[0]);
    }
  }, [dryRun, testUser, testUserOptions]);

  const refetchUsers = useCallback(() => {
    if (id) {
      dispatch(getBatchReportDetails(id));
      dispatch(getReportPossibleUsers(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  const dataGrid = [
    {
      label: 'Name',
      value: report?.report_name,
    },
    {
      label: 'Description',
      value: report?.report_description,
    },
    {
      label: 'Short ID',
      value: report?.short_id,
    },
    {
      label: 'Schedule',
      value: report?.schedule,
    },
    {
      label: 'Recipients',
      value: report?.recipient_count,
    },
  ];

  const handleGetSubscribers = () => {
    if (id) {
      dispatch(getReportPossibleUsers(id));
    }

    toggleModal('showSubscribersForm');
  };

  const handleChangeTab = (event, newValue) => {
    navigate(` /admin/reporting/batch/${id}/details/${newValue}`);
  };

  const handleRunReport = async () => {
    setConfirmOpen(false);
    setRunning(true);
    try {
      await dispatch(
        batchReportCreate({
          organization_id: userData?.organization_id,
          send_email: true,
          short_id: report?.short_id,
          dry_run: dryRun || undefined,
          test_user_id: dryRun ? testUser?.id : undefined,
        }),
      ).unwrap();
      if (userData?.organization_id && id) {
        dispatch(batchReportRunGetList({ organizationId: userData.organization_id, reportId: id }));
      }
      toast.success(dryRun ? 'Dry run completed — check the test inbox' : 'Report was successfully created', toastOptions);
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
    setRunning(false);
  };

  return (
    <>
      {fetching && <LinearProgress />}
      <GridCells container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <CardFlex>
            <CardHeaderFlex title="Report" />
            <CardContent>
              <ListTypography data={dataGrid} fetching={fetching} />
            </CardContent>
            <CardActions sx={{ px: 4, pb: 4, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              {isDistribution && (
                <>
                  <FormControlLabel
                    control={<Checkbox checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />}
                    label="Dry run — send all emails to a test user only"
                  />
                  {dryRun && (
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 320 }}
                      options={testUserOptions}
                      value={testUser}
                      onChange={(_event, value) => setTestUser(value)}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => <TextField {...params} label="Send everything to" />}
                    />
                  )}
                </>
              )}
              <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Button
                  variant={dryRun ? 'outlined' : 'contained'}
                  onClick={() => setConfirmOpen(true)}
                  startIcon={running && <CircularProgress size={22} color="inherit" />}
                  disabled={running || (dryRun && !testUser)}
                >
                  {running ? 'Running' : dryRun ? 'Run dry run' : 'Run'}
                </Button>
                {dryRun && (
                  <Typography variant="caption" color="text.secondary">
                    no real emails will be sent
                  </Typography>
                )}
              </Box>
            </CardActions>
          </CardFlex>
        </Grid>
        <Grid item xs={12} sm={6}>
          <CardFlex style={{ display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex
              title="Subscribers"
              legend={
                <Button variant="contained" size="small" sx={{ top: -2 }} onClick={handleGetSubscribers}>
                  Subscribers
                </Button>
              }
            />
            <CardContent sx={{ height: '100%', position: 'relative' }}>
              <Box component="div" sx={{ position: { sm: 'absolute' }, top: 8, right: 0, bottom: 16, left: 16, overflow: 'auto', pr: { sm: 2 } }}>
                <Table size="small">
                  <TableBody>
                    {report?.recipients?.map((item) => (
                      <TableRow key={item.email}>
                        <TableCell>
                          <Typography variant="subtitle2" component="strong">
                            {item.first_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" component="strong">
                            {item.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" component="strong">
                            {item.email}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', flexDirection: 'column' }}>
              <Typography variant="subtitle1" component="div">
                {report?.recipients?.length ?? 0} {pluralize('record', report?.recipients?.length ?? 0)}
              </Typography>
              {isDistribution && (
                <Typography variant="caption" color="text.secondary">
                  For this job, these recipients are copied on every project manager&apos;s email and receive the Unassigned jobsites email.
                </Typography>
              )}
            </CardActions>
          </CardFlex>
        </Grid>
      </GridCells>
      <Card sx={{ mt: 8 }}>
        <CardHeaderTabs
          title={
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleChangeTab} variant="scrollable" scrollButtons="auto">
                <Tab label="History" />
              </Tabs>
            </Box>
          }
          sx={{
            '& .MuiCardHeader-content': {
              width: 0,
            },
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <TabPanel value={tabValue} index={0}>
            <ReportHistory />
          </TabPanel>
        </CardContent>
      </Card>
      <SubscribersForm
        open={modals.showSubscribersForm}
        onClose={() => toggleModal('showSubscribersForm')}
        users={users}
        loading={fetchingUsers}
        recipients={report?.recipients}
        reportId={report?.report_id}
        refetchUsers={refetchUsers}
      />
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{dryRun ? 'Run dry run?' : 'Run report?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dryRun
              ? `All emails will be sent only to ${testUser?.label ?? 'the selected test user'}. Real recipients will not be contacted.`
              : isDistribution
              ? 'This will email each project manager their job-site table with the subscribers copied, and the Unassigned list to the subscribers.'
              : 'This will generate the report and email it to the configured recipients.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRunReport}>
            {dryRun ? 'Run dry run' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
